import { json_headers, request } from './common';

function basePath() {
  return window.configs.API_HOST + '/api/experiences/corporate-events';
}

export interface Company {
  id: string;
  name: string;
  logoId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Event {
  id: string;
  companyId: string;
  companyName?: string;
  createdBy: string;
  name: string;
  description?: string;
  heroImageId?: string;
  hotelName?: string;
  roomType?: string;
  roomInclusions?: string;
  checkinDate?: string;
  checkoutDate?: string;
  locationCity?: string;
  locationCountry?: string;
  itineraryHighlights?: string;
  agendaFileUrl?: string;
  dressCode?: string;
  faqsUrl?: string;
  isPublicLink: boolean;
  eventCode?: string;
  allowEditRegistration: boolean;
  allowTripExtension?: boolean;
  allowGuest?: boolean;
  allowPreferredAirline?: boolean;
  allowPreferredSeatClass?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateEventParams {
  companyId: string;
  createdBy?: string;
  name: string;
  description?: string;
  heroImageId?: string;
  hotelName?: string;
  roomType?: string;
  roomInclusions?: string;
  checkinDate?: string;
  checkoutDate?: string;
  locationCity?: string;
  locationCountry?: string;
  itineraryHighlights?: string;
  agendaFileUrl?: string;
  dressCode?: string;
  faqsUrl?: string;
  isPublicLink?: boolean;
  eventCode?: string;
  allowEditRegistration?: boolean;
  allowTripExtension?: boolean;
  allowGuest?: boolean;
  allowPreferredAirline?: boolean;
  allowPreferredSeatClass?: boolean;
}

export type UpdateEventParams = Partial<CreateEventParams>;

// Companies
export async function getCompanies(): Promise<Array<Company>> {
  const response = await request(`${basePath()}/companies`, { method: 'GET' });
  return response.result || response;
}

export async function getCompanyById(id: string): Promise<Company> {
  const response = await request(`${basePath()}/companies/${id}`, { method: 'GET' });
  return response.result || response;
}

export async function createCompany(data: { name: string; logoId?: string }): Promise<Company> {
  const response = await request(`${basePath()}/companies`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(data),
  });
  return response.result || response;
}

export async function updateCompany(id: string, data: Partial<Company>): Promise<Company> {
  const response = await request(`${basePath()}/companies/${id}`, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(data),
  });
  return response.result || response;
}

export async function deleteCompany(id: string): Promise<void> {
  await request(`${basePath()}/companies/${id}`, { method: 'DELETE' });
}

// Events
export async function getEvents(): Promise<Array<Event>> {
  const response = await request(`${basePath()}/events`, { method: 'GET' });
  return response.result || response;
}

export async function getEventById(id: string): Promise<Event> {
  const response = await request(`${basePath()}/events/${id}`, { method: 'GET' });
  return response.result || response;
}

export async function createEvent(data: CreateEventParams): Promise<Event> {
  const response = await request(`${basePath()}/events`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(data),
  });
  return response.result || response;
}

export async function updateEvent(id: string, data: UpdateEventParams): Promise<Event> {
  const response = await request(`${basePath()}/events/${id}`, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(data),
  });
  return response.result || response;
}

export async function deleteEvent(id: string): Promise<void> {
  await request(`${basePath()}/events/${id}`, { method: 'DELETE' });
}

// Registration Custom Questions
export interface RegistrationCustomQuestionOption {
  id?: string;
  questionId?: string;
  label: string;
  value: string;
  sortOrder?: number;
}

export interface OptionsDiff {
  toCreate: Array<RegistrationCustomQuestionOption>;
  toUpdate: Array<RegistrationCustomQuestionOption>;
  toDelete: Array<RegistrationCustomQuestionOption>;
}

export interface RegistrationCustomQuestion {
  id?: string;
  eventId?: string;
  label: string;
  description?: string;
  fieldName: string;
  fieldType: string;
  isRequired: boolean;
  sortOrder?: number;
  isSensitive?: boolean;
  options?: Array<RegistrationCustomQuestionOption>;
  optionsDiff?: OptionsDiff;
}

export interface CreateRegistrationCustomQuestionParams {
  eventId: string;
  label: string;
  description?: string;
  fieldName: string;
  fieldType: string;
  isRequired: boolean;
  sortOrder?: number;
  isSensitive?: boolean;
}

export interface CreateRegistrationCustomQuestionOptionParams {
  questionId: string;
  label: string;
  value: string;
  sortOrder?: number;
}

export type UpdateRegistrationCustomQuestionParams = Partial<CreateRegistrationCustomQuestionParams>;
export type UpdateRegistrationCustomQuestionOptionParams = Partial<CreateRegistrationCustomQuestionOptionParams>;

export async function getRegistrationCustomQuestionsByEventId(
  eventId: string,
): Promise<Array<RegistrationCustomQuestion>> {
  const response = await request(`${basePath()}/events/${eventId}/registration-custom-questions`, { method: 'GET' });
  return response.result || response;
}

export async function createRegistrationCustomQuestion(
  data: CreateRegistrationCustomQuestionParams,
): Promise<RegistrationCustomQuestion> {
  const response = await request(`${basePath()}/registration-custom-questions`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(data),
  });
  return response.result || response;
}

export async function updateRegistrationCustomQuestion(
  id: string,
  data: UpdateRegistrationCustomQuestionParams,
): Promise<RegistrationCustomQuestion> {
  const response = await request(`${basePath()}/registration-custom-questions/${id}`, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(data),
  });
  return response.result || response;
}

export async function deleteRegistrationCustomQuestion(id: string): Promise<void> {
  await request(`${basePath()}/registration-custom-questions/${id}`, { method: 'DELETE' });
}

// Registration Custom Question Options
export async function getRegistrationCustomQuestionOptionsByQuestionId(
  questionId: string,
): Promise<Array<RegistrationCustomQuestionOption>> {
  const response = await request(`${basePath()}/registration-custom-questions/${questionId}/options`, {
    method: 'GET',
  });
  return response.result || response;
}

export async function createRegistrationCustomQuestionOption(
  data: CreateRegistrationCustomQuestionOptionParams,
): Promise<RegistrationCustomQuestionOption> {
  const response = await request(`${basePath()}/registration-custom-question-options`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(data),
  });
  return response.result || response;
}

export async function updateRegistrationCustomQuestionOption(
  id: string,
  data: UpdateRegistrationCustomQuestionOptionParams,
): Promise<RegistrationCustomQuestionOption> {
  const response = await request(`${basePath()}/registration-custom-question-options/${id}`, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(data),
  });
  return response.result || response;
}

export async function deleteRegistrationCustomQuestionOption(id: string): Promise<void> {
  await request(`${basePath()}/registration-custom-question-options/${id}`, { method: 'DELETE' });
}

// Event Invited Users
export interface InvitedUser {
  id?: string;
  eventId?: string;
  fullName?: string;
  email: string;
  department?: string;
  role?: string;
  notes?: string;
  hasAnalyticsAccess?: boolean;
  invitedAt?: string;
}

export interface CreateInvitedUserParams {
  fullName?: string;
  email: string;
  department?: string;
  role?: string;
  notes?: string;
  hasAnalyticsAccess?: boolean;
}

export async function getInvitedUsersByEventId(eventId: string): Promise<Array<InvitedUser>> {
  const response = await request(`${basePath()}/events/${eventId}/invited-users`, { method: 'GET' });
  return response.result || response;
}

export async function createInvitedUser(eventId: string, user: CreateInvitedUserParams): Promise<InvitedUser> {
  const response = await request(`${basePath()}/events/${eventId}/invited-user`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(user),
  });
  return response.result || response;
}

export async function updateInvitedUser(id: string, user: Partial<CreateInvitedUserParams>): Promise<InvitedUser> {
  const response = await request(`${basePath()}/invited-users/${id}`, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(user),
  });
  return response.result || response;
}

// Batch operations - kept for future file import functionality
export async function upsertInvitedUsers(
  eventId: string,
  users: Array<CreateInvitedUserParams>,
): Promise<Array<InvitedUser>> {
  const response = await request(`${basePath()}/events/${eventId}/invited-users`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify({ users }),
  });
  return response.result || response;
}

export async function removeInvitedUsers(eventId: string, emails: Array<string>): Promise<void> {
  await request(`${basePath()}/events/${eventId}/invited-users`, {
    method: 'DELETE',
    headers: json_headers,
    body: JSON.stringify({ emails }),
  });
}

const CorporateEventsService = {
  // Companies
  getCompanies,
  getCompanyById,
  createCompany,
  updateCompany,
  deleteCompany,
  // Events
  getEvents,
  getEventById,
  createEvent,
  updateEvent,
  deleteEvent,
  // Registration Custom Questions
  getRegistrationCustomQuestionsByEventId,
  createRegistrationCustomQuestion,
  updateRegistrationCustomQuestion,
  deleteRegistrationCustomQuestion,
  // Registration Custom Question Options
  getRegistrationCustomQuestionOptionsByQuestionId,
  createRegistrationCustomQuestionOption,
  updateRegistrationCustomQuestionOption,
  deleteRegistrationCustomQuestionOption,
  // Event Invited Users
  getInvitedUsersByEventId,
  createInvitedUser,
  updateInvitedUser,
  upsertInvitedUsers,
  removeInvitedUsers,
};

export default CorporateEventsService;
